import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  Calendar, 
  DollarSign, 
  Mail, 
  Phone, 
  CheckCircle,
  ExternalLink,
  MessageCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLeaseStore } from '../stores/leaseStore';
import { useMessageStore } from '../stores/messageStore';
import { formatDate, formatPrice } from '../utils/formatters';

const ActiveLeases = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getActiveLeases } = useLeaseStore();
  const { createConversation } = useMessageStore();

  if (!user) return null;

  const activeLeases = getActiveLeases(user.id);

  const handleMessage = (lease: any) => {
    let conversationId = lease.conversationId;
    
    // If no conversation exists, create one
    if (!conversationId) {
      const recipientId = user.role === 'renter' ? lease.listing.ownerId : lease.applicant.id;
      const recipientName = user.role === 'renter' ? lease.listing.ownerName : lease.applicant.name;
      
      conversationId = createConversation({
        participants: [user.id, recipientId],
        recipientId,
        recipientName,
        listingId: lease.listingId,
        listingTitle: lease.listing.title
      });
    }

    // Navigate to the conversation
    navigate(`/messages?conversation=${conversationId}`);
  };

  if (activeLeases.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <Building2 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Leases</h3>
        <p className="text-gray-600">
          {user.role === 'owner' 
            ? 'When you approve applications and receive deposits, they will appear here.'
            : 'When your applications are approved and you pay the deposit, they will appear here.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {activeLeases.map((lease) => (
        <div key={lease.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="flex flex-col md:flex-row">
            {/* Property Image - Fixed aspect ratio */}
            <div className="w-full md:w-[300px] flex-shrink-0">
              <Link 
                to={`/listings/${lease.listingId}`}
                className="block relative aspect-[4/3] group overflow-hidden"
              >
                <img
                  src={lease.listing.image}
                  alt={lease.listing.title}
                  className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <ExternalLink className="h-6 w-6 text-white" />
                </div>
              </Link>
            </div>

            {/* Lease Details */}
            <div className="flex-1 p-6">
              {/* Header with Deposit Status */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <Link 
                    to={`/listings/${lease.listingId}`}
                    className="text-lg font-medium text-gray-900 hover:text-indigo-600"
                  >
                    {lease.listing.title}
                  </Link>
                  <p className="text-gray-600">{lease.listing.location}</p>
                </div>
                <div className="flex items-center bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm">
                  <CheckCircle className="h-4 w-4 mr-1.5" />
                  Deposit Paid
                </div>
              </div>

              {/* Key Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <div className="flex items-center text-sm text-gray-500 mb-1">
                    <Calendar className="h-4 w-4 mr-2" />
                    Lease Period
                  </div>
                  <p className="font-medium">
                    {formatDate(lease.moveInDate)} - {formatDate(lease.moveOutDate)}
                  </p>
                </div>

                <div>
                  <div className="flex items-center text-sm text-gray-500 mb-1">
                    <DollarSign className="h-4 w-4 mr-2" />
                    Monthly Rent
                  </div>
                  <p className="font-medium">{formatPrice(lease.listing.price)}</p>
                </div>

                <div>
                  <div className="flex items-center text-sm text-gray-500 mb-1">
                    <DollarSign className="h-4 w-4 mr-2" />
                    Security Deposit
                  </div>
                  <p className="font-medium">
                    {lease.securityDeposit ? formatPrice(lease.securityDeposit) : 'Not specified'}
                  </p>
                </div>
              </div>

              {/* Contact Information */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  {user.role === 'renter' ? 'Property Owner' : 'Tenant'} Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Name</p>
                    <p className="font-medium">
                      {user.role === 'renter' ? lease.listing.ownerName : lease.applicant.name}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 text-gray-400 mr-2" />
                      <p className="text-sm">
                        {user.role === 'renter' ? lease.listing.ownerEmail : lease.applicant.email}
                      </p>
                    </div>
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 text-gray-400 mr-2" />
                      <p className="text-sm">
                        {user.role === 'renter' ? lease.listing.ownerPhone : lease.applicant.phone}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Details */}
              {(lease.moveInInstructions || lease.houseRules) && (
                <div className="border-t mt-4 pt-4 space-y-4">
                  {lease.moveInInstructions && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Move-in Instructions</h4>
                      <p className="text-sm text-gray-600">{lease.moveInInstructions}</p>
                    </div>
                  )}
                  {lease.houseRules && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">House Rules</h4>
                      <p className="text-sm text-gray-600">{lease.houseRules}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Message Button */}
              <div className="flex justify-end mt-4 pt-4 border-t">
                <button
                  onClick={() => handleMessage(lease)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Message {user.role === 'renter' ? 'Owner' : 'Tenant'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ActiveLeases;